<template>
  <div class="tourism-photography-package-list-container">
    <h1 class="page-title">旅拍套餐列表</h1>
    <el-card class="content-card">
      <div class="card-header">
        <h2>旅拍套餐列表</h2>
        <el-button type="primary" @click="handleAddPackage">添加旅拍套餐</el-button>
      </div>
      <div class="card-content">
        <!-- 搜索筛选区域 -->
        <div class="search-filter-section">
          <el-form :inline="true" :model="searchForm" size="small">
            <el-form-item label="套餐名称">
              <el-input v-model="searchForm.search" placeholder="请输入套餐名称" clearable />
            </el-form-item>
            <el-form-item label="状态">
              <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
                <el-option label="启用" :value="1" />
                <el-option label="禁用" :value="0" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="() => { packages.current_page = 1; fetchData(); }">搜索</el-button>
              <el-button @click="() => { searchForm.search = ''; searchForm.status = ''; packages.current_page = 1; fetchData(); }">重置</el-button>
            </el-form-item>
          </el-form>
        </div>

        <!-- 数据表格区域 -->
        <div class="table-section">
          <el-table v-loading="loading" :data="packages.data" style="width: 100%" border>
            <el-table-column prop="id" label="ID" width="80" align="center" />
            <el-table-column prop="title" label="套餐名称" min-width="200" />
            <el-table-column prop="price" label="价格" width="120" align="right">
              <template #default="scope">
                {{ scope.row.price.toFixed(2) }} 元
              </template>
            </el-table-column>
            <el-table-column prop="cover_image" label="封面图片" width="120" align="center">
              <template #default="scope">
                <el-image
                  v-if="scope.row.cover_image"
                  :src="scope.row.cover_image"
                  :preview-src-list="[scope.row.cover_image]"
                  fit="cover"
                  style="width: 60px; height: 60px; border-radius: 4px"
                />
                <span v-else>无</span>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="100" align="center">
              <template #default="scope">
                <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
                  {{ scope.row.status === 1 ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="sort_order" label="排序" width="100" align="center" />
            <el-table-column prop="created_at" label="创建时间" width="180" align="center" />
            <el-table-column label="操作" width="200" align="center" fixed="right">
              <template #default="scope">
                <el-button size="small" type="primary" @click="handleEditPackage(scope.row)">
                  编辑
                </el-button>
                <el-button size="small" type="danger" @click="handleDeletePackage(scope.row.id)">
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分页区域 -->
          <div class="pagination-section">
            <el-pagination
              :current-page="packages.current_page"
              :page-size="packages.per_page"
              :page-sizes="[10, 20, 50, 100]"
              layout="total, sizes, prev, pager, next, jumper"
              :total="packages.total"
              @size-change="(size) => { packages.current_page = 1; packages.per_page = size; fetchData(); }"
              @current-change="(current) => { packages.current_page = current; fetchData(); }"
            />
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { getPhotoPackages } from '@/api/photo';
import { ElMessage } from 'element-plus';

// 状态管理
const loading = ref(false);
const packages = ref({
  data: [],
  total: 0,
  current_page: 1,
  per_page: 10
});

// 搜索表单
const searchForm = ref({
  search: '',
  status: ''
});

// 获取数据
const fetchData = async () => {
  loading.value = true;
  try {
    const params = {
      page: packages.value.current_page,
      per_page: packages.value.per_page,
      search: searchForm.value.search,
      status: searchForm.value.status || undefined
    };
    const response = await getPhotoPackages(params);
    packages.value = response.data;
  } catch (error) {
    console.error('获取旅拍套餐列表失败:', error);
    ElMessage.error('获取旅拍套餐列表失败');
  } finally {
    loading.value = false;
  }
};

// 页面挂载时获取数据
onMounted(() => {
  fetchData();
});

// 添加旅拍套餐
/* eslint-disable-next-line no-unused-vars */
const handleAddPackage = () => {
  ElMessage.info('添加旅拍套餐功能开发中');
};

// 编辑旅拍套餐
/* eslint-disable-next-line no-unused-vars */
const handleEditPackage = (packageInfo) => {
  ElMessage.info(`编辑旅拍套餐 ID: ${packageInfo.id}`);
};

// 删除旅拍套餐
/* eslint-disable-next-line no-unused-vars */
const handleDeletePackage = (packageId) => {
  ElMessage.info(`删除旅拍套餐 ID: ${packageId}`);
};
</script>

<style scoped>
.tourism-photography-package-list-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.card-content {
  padding: 20px 0;
}

.search-filter-section {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.table-section {
  margin-top: 20px;
}

.pagination-section {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>